import React, { Component } from 'react';
import { saveAs } from 'file-saver';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { image_error_replace } from '../../../../util_functions';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2020; year <= currentYear + 1; year += 1) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
};

const YEARS = generateYears();

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

class TeacherPayments extends Component {
  state = {
    loading: false,
    dataLoaded: false,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    total_teachers: 0,
    teacher_summaries: []
  };

  loadData = () => {
    this.setState({ loading: true });
    const { month, year } = this.state;
    const url = get_api_url(
      `/v2/cms_user/teachers/payout-details/time/${month}/${year}`,
      null,
      true
    );
    get_api(url, true)
      .then((response) => {
        this.setState({
          loading: false,
          dataLoaded: true,
          total_teachers: response.data.total_teachers || 0,
          teacher_summaries: response.data.teacher_summaries || []
        });
      })
      .catch((error) => {
        console.error('Error fetching teacher payments:', error);
        this.setState({
          loading: false,
          dataLoaded: true,
          teacher_summaries: [],
          total_teachers: 0
        });
      });
  };

  handleYearChange = (e) => {
    this.setState({ year: parseInt(e.target.value, 10) });
  };

  handleMonthChange = (e) => {
    this.setState({ month: parseInt(e.target.value, 10) });
  };

  getMonthName = () => {
    const monthObj = MONTHS.find((m) => m.value === this.state.month);
    return monthObj ? monthObj.label : '';
  };

  calculateTotals = () => {
    const { teacher_summaries } = this.state;
    return teacher_summaries.reduce(
      (acc, teacher) => ({
        totalSessionsPayout: acc.totalSessionsPayout + (teacher.total_sessions_payout || 0),
        totalPenalties: acc.totalPenalties + (teacher.total_penalties || 0),
        totalWaivers: acc.totalWaivers + (teacher.total_waivers || 0),
        totalIncentives: acc.totalIncentives + (teacher.total_incentives || 0),
        totalFinalPayout: acc.totalFinalPayout + (teacher.final_payout || 0)
      }),
      {
        totalSessionsPayout: 0,
        totalPenalties: 0,
        totalWaivers: 0,
        totalIncentives: 0,
        totalFinalPayout: 0
      }
    );
  };

  downloadCSV = () => {
    const { teacher_summaries, month, year } = this.state;
    if (teacher_summaries.length === 0) return;

    const headers = [
      'Teacher Name',
      'Teacher UUID',
      'Currency',
      'Sessions Payout',
      'Penalties',
      'Waivers',
      'Incentives',
      'Final Payout'
    ];

    const rows = teacher_summaries.map((teacher) => [
      `"${teacher.teacher_name}"`,
      teacher.teacher_uuid,
      teacher.currency,
      teacher.total_sessions_payout,
      teacher.total_penalties,
      teacher.total_waivers,
      teacher.total_incentives,
      teacher.final_payout
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `teacher_payout_summary_${month}_${year}.csv`);
  };

  openTeacherDetail = (teacherUuid) => {
    const { month, year } = this.state;
    window.open(`/teacher-payments/teacher/${teacherUuid}/time/${month}/${year}`, '_blank');
  };

  renderFilters = () => {
    const { loading, dataLoaded, teacher_summaries } = this.state;
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap gap-2 sm:gap-3 items-end">
          <div className="flex-1 min-w-[100px] max-w-[140px]">
            <label htmlFor="year-select" className="block text-xs font-semibold text-gray-700 mb-1">
              Year
            </label>
            <select
              id="year-select"
              value={this.state.year}
              onChange={this.handleYearChange}
              disabled={loading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {YEARS.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[100px] max-w-[140px]">
            <label
              htmlFor="month-select"
              className="block text-xs font-semibold text-gray-700 mb-1"
            >
              Month
            </label>
            <select
              id="month-select"
              value={this.state.month}
              onChange={this.handleMonthChange}
              disabled={loading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {MONTHS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={this.loadData}
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {loading ? 'Loading...' : 'Load'}
          </button>
          {dataLoaded && teacher_summaries.length > 0 && (
            <button
              type="button"
              onClick={this.downloadCSV}
              className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              CSV
            </button>
          )}
        </div>
      </div>
    );
  };

  renderSummaryCards = () => {
    const { total_teachers } = this.state;
    const totals = this.calculateTotals();

    return (
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 mb-4">
        {/* Teachers */}
        <div className="bg-blue-600 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-blue-100 font-medium">Teachers</p>
          <p className="text-lg sm:text-xl font-bold text-white">{total_teachers}</p>
        </div>
        {/* Sessions Payout */}
        <div className="bg-slate-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-slate-500 font-medium">Sessions</p>
          <p className="text-sm sm:text-base font-bold text-slate-800 truncate">
            {formatCurrency(totals.totalSessionsPayout, 'INR')}
          </p>
        </div>
        {/* Penalties */}
        <div className="bg-red-600 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-red-100 font-medium">Penalties</p>
          <p className="text-sm sm:text-base font-bold text-white truncate">
            −{formatCurrency(totals.totalPenalties, 'INR')}
          </p>
        </div>
        {/* Incentives */}
        <div className="bg-emerald-100 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-emerald-600 font-medium">Incentives</p>
          <p className="text-sm sm:text-base font-bold text-emerald-800 truncate">
            +{formatCurrency(totals.totalIncentives, 'INR')}
          </p>
        </div>
        {/* Waivers */}
        <div className="bg-amber-100 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-amber-600 font-medium">Waivers</p>
          <p className="text-sm sm:text-base font-bold text-amber-800 truncate">
            {formatCurrency(totals.totalWaivers, 'INR')}
          </p>
        </div>
        {/* Final */}
        <div className="bg-purple-600 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs text-purple-100 font-medium">Final</p>
          <p className="text-sm sm:text-base font-bold text-white truncate">
            {formatCurrency(totals.totalFinalPayout, 'INR')}
          </p>
        </div>
      </div>
    );
  };

  renderMobileCard = (teacher) => (
    <div
      key={teacher.teacher_uuid}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        <img
          src={teacher.teacher_image}
          alt=""
          onError={image_error_replace}
          className="w-8 h-8 rounded-full object-cover border border-gray-300"
        />
        <a
          href={`/teacherprofile?id=${teacher.teacher_uuid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-sm font-semibold text-blue-700 hover:text-blue-900 truncate"
        >
          {teacher.teacher_name}
        </a>
        <span className="text-xs text-gray-500 font-medium">{teacher.currency}</span>
      </div>

      {/* Body */}
      <div className="px-3 py-2">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Sessions</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(teacher.total_sessions_payout, teacher.currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Penalty</span>
            <span className="font-semibold text-red-700">
              {teacher.total_penalties > 0
                ? `−${formatCurrency(teacher.total_penalties, teacher.currency)}`
                : '−'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Incentive</span>
            <span className="font-semibold text-emerald-700">
              {teacher.total_incentives > 0
                ? `+${formatCurrency(teacher.total_incentives, teacher.currency)}`
                : '−'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Waiver</span>
            <span className="font-semibold text-amber-700">
              {teacher.total_waivers > 0
                ? formatCurrency(teacher.total_waivers, teacher.currency)
                : '−'}
            </span>
          </div>
        </div>

        {/* Final + Action */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">Final: </span>
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(teacher.final_payout, teacher.currency)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => this.openTeacherDetail(teacher.teacher_uuid)}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Details →
          </button>
        </div>
      </div>
    </div>
  );

  renderTable = () => {
    const { teacher_summaries } = this.state;

    if (teacher_summaries.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 py-12 px-4 text-center">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-600 font-medium">No payout data</p>
          <p className="text-gray-500 text-sm mt-1">No records found for the selected period.</p>
        </div>
      );
    }

    return (
      <>
        {/* Mobile */}
        <div className="lg:hidden space-y-3">
          {teacher_summaries.map((teacher) => this.renderMobileCard(teacher))}
        </div>

        {/* Desktop */}
        <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Teacher
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Currency
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Sessions
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Penalties
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Waivers
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Incentives
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Final
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wide"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teacher_summaries.map((teacher, index) => (
                  <tr
                    key={teacher.teacher_uuid}
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <img
                          src={teacher.teacher_image}
                          alt=""
                          onError={image_error_replace}
                          className="w-8 h-8 rounded-full object-cover border border-gray-200"
                        />
                        <a
                          href={`/teacherprofile?id=${teacher.teacher_uuid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 hover:text-blue-900 font-medium hover:underline"
                        >
                          {teacher.teacher_name}
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-center text-gray-600">
                      {teacher.currency}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-medium text-gray-900">
                      {formatCurrency(teacher.total_sessions_payout, teacher.currency)}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-semibold text-red-700">
                      {teacher.total_penalties > 0
                        ? `−${formatCurrency(teacher.total_penalties, teacher.currency)}`
                        : '−'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-semibold text-amber-700">
                      {teacher.total_waivers > 0
                        ? formatCurrency(teacher.total_waivers, teacher.currency)
                        : '−'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-semibold text-emerald-700">
                      {teacher.total_incentives > 0
                        ? `+${formatCurrency(teacher.total_incentives, teacher.currency)}`
                        : '−'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-bold text-gray-900">
                      {formatCurrency(teacher.final_payout, teacher.currency)}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-center">
                      <button
                        type="button"
                        onClick={() => this.openTeacherDetail(teacher.teacher_uuid)}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700"
                      >
                        Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12" role="status">
      <svg
        className="animate-spin h-8 w-8 text-blue-600 mb-3"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="text-gray-600 text-sm font-medium">Loading payout data...</p>
    </div>
  );

  render() {
    const { loading, dataLoaded, teacher_summaries } = this.state;

    return (
      <main className="bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-4 sm:py-4">
          {/* Header */}
          <header className="mb-3 sm:mb-4">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Teacher Payouts</h1>
            <p className="text-sm text-gray-600">
              {dataLoaded
                ? `${teacher_summaries.length} teachers • ${this.getMonthName()} ${this.state.year}`
                : 'Select month/year and click Load'}
            </p>
          </header>

          {this.renderFilters()}

          {loading
            ? this.renderLoadingState()
            : dataLoaded && (
                <>
                  {this.renderSummaryCards()}
                  {this.renderTable()}
                </>
              )}
        </div>
      </main>
    );
  }
}

export default TeacherPayments;
